// Card payments on the public booking forms (pages/bookings/newPublic.js and
// newPublicModal.js). The booking is created first, then charged:
// payment_intent -> stripe.confirmCardPayment -> payment_confirmation.

export const CARD_PAYMENT_METHOD = 'dr/cr';

// Card payments are offered only when the company has them switched on, a
// connected Stripe account, and Stripe.js loaded. Anything missing: hidden.
export const cardPaymentsAvailable = (settings) => Boolean(
  settings && settings.card_payments === true && settings.stripe_account &&
  typeof window !== 'undefined' && window.Stripe && window.stripe_api_key
);

// Cards are for one-time bookings: a card-paid booking is invoiced on its job
// when the company confirms it, and recurring bookings get a service agreement
// instead. A frequency counts as recurring unless it's "One Time".
export const isRecurringFrequency = (frequency) => Boolean(
  frequency && frequency.title && frequency.title !== 'One Time'
);

export const recurringFrequencyChosen = (frequencies, frequencyId) => Boolean(
  frequencyId && (frequencies || []).some((frequency) => String(frequency.id) === String(frequencyId) && isRecurringFrequency(frequency))
);

// What a card payment charges: the booking's total plus the company's default
// tax (payment settings), as the server prices it. Amounts in dollars.
//   { taxLabel: 'HST (13%)' | null, tax: 13.0, charge: 113.0 }
export const cardCharge = (total, settings) => {
  const subtotal = parseFloat(total) || 0;
  const tax = settings && settings.tax;
  const percentage = tax ? parseFloat(tax.percentage) : NaN;
  if (!tax || !(percentage > 0)) return { taxLabel: null, tax: 0, charge: subtotal };
  const taxAmount = Math.round(subtotal * percentage) / 100;
  return { taxLabel: `${tax.name} (${tax.percentage}%)`, tax: taxAmount, charge: Math.round((subtotal + taxAmount) * 100) / 100 };
};

// One Stripe instance per connected account. Elements, the card field and
// confirmCardPayment must all use this same instance.
const stripeInstances = {};
export const stripeForAccount = (stripeAccount) => {
  if (!stripeAccount || typeof window === 'undefined' || !window.Stripe || !window.stripe_api_key) return null;
  if (!stripeInstances[stripeAccount]) {
    stripeInstances[stripeAccount] = window.Stripe(window.stripe_api_key, { stripeAccount });
  }
  return stripeInstances[stripeAccount];
};

// A readable message from an API (axios) error, a Stripe error or an Error.
export const paymentErrorMessage = (error, fallback = 'The payment didn\'t go through. Please try again.') => {
  if (!error) return fallback;
  const data = error.response && error.response.data;
  if (data) {
    const errors = data.errors || data.error;
    if (Array.isArray(errors) && errors.length > 0) return errors.join(', ');
    if (errors && typeof errors === 'string') return errors;
  }
  if (error.message && !error.response) return error.message;
  return fallback;
};

// Charges a booking that already exists. `progress` is kept by the page for
// that booking ({ charged: false }) so a retry after the card was charged only
// re-sends payment_confirmation and never charges twice. A payment the bank is
// still processing is recorded by the server when it completes (webhook).
// Resolves with { amountCents, currency, booking }.
export const payPublicBooking = (bookingStore, cardApi, bookingId, progress = {}) => {
  if (!bookingId || bookingId === true) {
    return Promise.reject(new Error('We couldn\'t start the payment for this booking. Please contact us.'));
  }

  const charge = () => {
    if (!cardApi || !cardApi.stripe || !cardApi.card) {
      return Promise.reject(new Error('Card payments are unavailable right now. Please try again.'));
    }
    return bookingStore.createPublicBookingPaymentIntent(bookingId)
      .then((response) => {
        const intent = (response && response.data) || {};
        if (!intent.client_secret) throw new Error('We couldn\'t start the payment. Please try again.');
        progress.amountCents = intent.amount_cents;
        progress.currency = intent.currency;
        return cardApi.stripe.confirmCardPayment(intent.client_secret, { payment_method: { card: cardApi.card } });
      })
      .then((result) => {
        if (result && result.error) {
          // Already paid on an earlier attempt: carry on to the confirmation.
          const paid = result.error.payment_intent && result.error.payment_intent.status === 'succeeded';
          if (!paid) throw new Error(result.error.message || 'Your card was declined.');
        } else if (result && result.paymentIntent && ['succeeded', 'processing'].indexOf(result.paymentIntent.status) === -1) {
          throw new Error('The payment wasn\'t completed. Please try again.');
        }
        progress.charged = true;
      });
  };

  return (progress.charged ? Promise.resolve() : charge())
    .then(() => bookingStore.confirmPublicBookingPayment(bookingId))
    .then((response) => {
      const booking = response && response.data && response.data.data;
      const attributes = (booking && booking.attributes) || {};
      const amountCents = attributes.amount_paid_cents != null ? attributes.amount_paid_cents : progress.amountCents;
      return { amountCents, currency: progress.currency, booking };
    });
};
